import React, { useEffect, useState } from "react";
import { BarChart3, Trophy, Calendar, Loader2, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * PollHistoryCard — admin archive of every weekly poll the rotation has
 * cycled through, newest first. Each row shows the question, week, total
 * votes, winning option, and the full per-option distribution.
 *
 * Backed by `GET /api/admin/polls/history`. Empty historic weeks are
 * filtered server-side so the table only surfaces rows with activity
 * (plus the current live week, even if it has no votes yet).
 */
export default function PollHistoryCard() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genCount, setGenCount] = useState(8);
  const [schedule, setSchedule] = useState(null);
  const [customPolls, setCustomPolls] = useState([]);

  const load = async () => {
    setRefreshing(true);
    try {
      const [hist, sched, custom] = await Promise.all([
        api.get("/admin/polls/history"),
        api.get("/polls/_schedule"),
        api.get("/admin/polls/custom"),
      ]);
      setRows(hist.data.history || []);
      setSchedule(sched.data || null);
      setCustomPolls(custom.data.polls || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load poll history");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    const n = Math.max(1, Math.min(20, Number(genCount) || 8));
    if (!window.confirm(`Generate ${n} new polls with Claude Sonnet 4.5 and append them to the rotation?`)) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/polls/generate", { count: n });
      toast.success(`Generated ${data.accepted_count} polls${data.rejected_count ? ` (${data.rejected_count} rejected)` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI poll generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const removeCustom = async (pollId) => {
    if (!window.confirm(`Remove this AI poll from the rotation? Past votes are kept.`)) return;
    try {
      await api.delete(`/admin/polls/custom/${encodeURIComponent(pollId)}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div
      className="bg-white border hairline rounded-none p-5 sm:p-6"
      data-testid="poll-history-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="eyebrow mb-1">Weekly polls · archive</div>
          <h3 className="font-display text-xl sm:text-2xl tracking-tight">
            Poll results history
          </h3>
          <p className="text-sm text-[#525252] mt-1">
            Every weekly poll the rotation has run, with final results. The
            current week stays editable — past weeks are sealed snapshots.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 mono text-[11px] uppercase tracking-[0.2em] text-[#1c1917] hover:underline disabled:opacity-50"
          data-testid="poll-history-refresh"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {error ? (
        <div className="bg-[#fee2e2] text-[#7f1d1d] px-3 py-2 text-sm" data-testid="poll-history-error">
          {error}
        </div>
      ) : null}

      {!rows ? (
        <div className="flex items-center gap-2 text-sm text-[#525252]" data-testid="poll-history-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-[#525252]" data-testid="poll-history-empty">
          No poll activity yet — once the launch week starts collecting
          votes, every week&apos;s results will be archived here.
        </div>
      ) : (
        <ol className="space-y-4" data-testid="poll-history-list">
          {rows.map((r) => {
            const sorted = [...(r.options || [])].sort((a, b) => b.count - a.count);
            const date = r.week_start ? new Date(r.week_start) : null;
            return (
              <li
                key={`${r.week_index}-${r.poll_id}`}
                className={`border hairline p-4 ${r.is_current ? "bg-[#fef3c7]" : "bg-[#fafaf9]"}`}
                data-testid={`poll-history-row-${r.week_index}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#7c2d12] inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Week {r.week_index}
                        {date ? ` · ${date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
                      </span>
                      {r.is_current ? (
                        <span className="mono text-[10px] uppercase tracking-[0.2em] bg-[#16a34a] text-white px-1.5 py-0.5">
                          Live
                        </span>
                      ) : null}
                      <span className="mono text-[10px] uppercase tracking-[0.2em] text-[#525252]">
                        {r.poll_id}
                      </span>
                    </div>
                    <div className="font-display text-base sm:text-lg text-[#1c1917]">
                      {r.question}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono text-[11px] uppercase tracking-[0.2em] text-[#525252]">
                      Total votes
                    </div>
                    <div className="font-display text-2xl text-[#1c1917] tabular-nums">
                      {r.total_votes}
                    </div>
                  </div>
                </div>

                {r.winner ? (
                  <div className="flex items-center gap-1.5 text-sm text-[#1c1917] mb-3">
                    <Trophy className="w-4 h-4 text-[#b45309]" />
                    <span>
                      Winner: <span className="font-bold">{r.winner}</span>
                      {" "}({sorted[0]?.pct}%)
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-sm text-[#525252] mb-3">
                    <BarChart3 className="w-4 h-4" /> No votes yet
                  </div>
                )}

                <ol className="space-y-1.5">
                  {sorted.map((o, i) => (
                    <li
                      key={o.key}
                      className="relative bg-white border hairline overflow-hidden"
                    >
                      <div
                        className="absolute inset-y-0 left-0 bg-[#fde68a]"
                        style={{ width: `${o.pct}%` }}
                        aria-hidden="true"
                      />
                      <div className="relative flex items-center justify-between gap-3 text-sm px-2.5 py-1.5">
                        <span className="text-[#1c1917]">
                          <span className="mono text-[11px] text-[#a3a3a3] mr-1.5">{i + 1}.</span>
                          {o.label}
                        </span>
                        <span className="mono text-[12px] text-[#1c1917] tabular-nums">
                          {o.pct}% · {o.count}
                        </span>
                      </div>
                    </li>
                  ))}
                </ol>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
